import React, {useState} from 'react';
import {RouteProp, useRoute} from '@react-navigation/native';

type LMSRouteParams = {courseKey?: string};

type View =
  | {kind: 'list'}
  | {kind: 'create'}
  | {kind: 'course'; uuid: string}
  | {kind: 'review'; uuid: string};

/**
 * Entry point for the Courses tab.
 *
 * Lives beside `index.tsx` rather than in it: `index.tsx` is the School of
 * Chess screen (an iframe onto ENV.LMS_URL), which is a different feature that
 * happens to share this folder. The two collided as an add/add conflict when
 * this branch merged master, and separating them here keeps both.
 *
 * Three views rather than three routes: the course player and the create
 * screen are both transient states of this tab, and keeping them here avoids
 * touching the navigator for each one. A `courseKey` route param still opens a
 * course directly, which is how a generated course is shared by link.
 *
 * DEVIATION from the plan: this screen's own markup uses plain react-native
 * primitives and the sub-screens are `require()`d lazily inside the branch
 * that needs them (a pattern already used elsewhere in this codebase, e.g.
 * `src/Screens/Visualisation/components/MemoryTrainer/index.tsx`). `CCText`
 * and `Box` are pre-existing files with their own internal `@/`-alias imports
 * that this work may not touch, and jest has no `moduleNameMapper` for `@`
 * (jest.config.js is equally untouchable) — so a *top-level* import of either
 * atom, or of anything that imports them, makes this module fail to load under
 * jest with "Cannot find module '@/Assets/Fonts/fontWeightToFamily'",
 * regardless of whether the component is ever rendered. Module resolution
 * happens at import time, not render time.
 */
export default function LMS() {
  const route = useRoute<RouteProp<Record<string, LMSRouteParams>, string>>();
  const routeKey = (route.params as LMSRouteParams)?.courseKey ?? '';
  const [view, setView] = useState<View>(
    routeKey ? {kind: 'course', uuid: routeKey} : {kind: 'list'},
  );

  if (view.kind === 'course') {
    const CourseChapterScreen = require('./CourseChapterScreen').default;
    return (
      <CourseChapterScreen
        bookUuid={view.uuid}
        onExit={() => setView({kind: 'list'})}
      />
    );
  }

  if (view.kind === 'review') {
    // The chat-first editor replaces the old review screen on this route.
    // ReviewScreen.tsx is kept, unrouted, until the editor has been used on
    // real courses.
    const EditorScreen = require('./editor/EditorScreen').default;
    return <EditorScreen bookUuid={view.uuid} onBack={() => setView({kind: 'list'})} />;
  }

  if (view.kind === 'create') {
    const CreateCourseScreen = require('./components/CreateCourseScreen').default;
    return (
      <CreateCourseScreen
        onBack={() => setView({kind: 'list'})}
        onOpen={(uuid: string) => setView({kind: 'course', uuid})}
      />
    );
  }

  // CourseListScreen decides for itself whether to offer Create Course: it
  // already fetches the list, and that response carries `can_create`. Reading
  // it here instead would mean importing the hook at the top of this module,
  // which drags ChessRestClient and its '@/'-aliased interceptor in with it
  // and breaks this module under jest — see the note above.
  const CourseListScreen = require('./components/CourseListScreen').default;
  return (
    <CourseListScreen
      onCreate={() => setView({kind: 'create'})}
      onOpen={(uuid: string) => setView({kind: 'course', uuid})}
      onReview={(uuid: string) => setView({kind: 'review', uuid})}
    />
  );
}
